import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';
import SaveTreeModal from '../SaveTreeModal';
import LoadTreeModal from '../LoadTreeModal';
import CanvasNameModal from '../CanvasNameModal';
import HelpModal from '../HelpModal';
import AlertModal from '../AlertModal';

import { exportTreeToJSON, downloadJSON } from '../../utils/treeExporter';
import { importTreeFromFile } from '../../utils/treeImporter';

import { GLASSMORPHIC_COLORS } from '../../utils/colorPalette';
import { trackEvent } from '../../utils/analytics';
import { useFeatureAccess } from '../../hooks/useQuota';
import { useAuth } from '../../hooks/useAuth';
import { needsCanvasNaming, handleCanvasNaming } from '../../utils/canvasNamingUtils';
import './MobileToolbar.css';

// --- ICONS ---
const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const ClearIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 22v-5"/>
    <path d="M14 19v-2"/>
    <path d="M14 2v4a2 2 0 0 0 2 2h4"/>
    <path d="M18 20v-3"/>
    <path d="M2 13h20"/>
    <path d="M20 13V7l-5-5H6a2 2 0 0 0-2 2v9"/>
    <path d="M6 20v-3"/>
  </svg>
);

const SaveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
  </svg>
);

const FolderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const HelpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);


interface MobileToolbarProps {
  isSideChatOpen: boolean;
}

const MobileToolbar: React.FC<MobileToolbarProps> = ({ isSideChatOpen }) => {
  const {
    addNote,
    showToast,
    state: { notes, canvasId },
  } = useWhiteboardStore();

  const { user } = useAuth();
  const { canCreateThreadStarter, canCreateTotalNote } = useFeatureAccess();

  const [showSaveTreeModal, setShowSaveTreeModal] = useState(false);
  const [showLoadTreeModal, setShowLoadTreeModal] = useState(false);
  const [showCanvasNameModal, setShowCanvasNameModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    title: '',
    message: '',
    buttonText: 'OK',
    buttonVariant: 'primary' as 'primary' | 'danger' | 'secondary' | 'success',
    isConfirmation: false,
    onConfirm: undefined as (() => void) | undefined
  });

  const [colorPopoverOpen, setColorPopoverOpen] = useState(false);
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  
  // Store the pending note color while canvas name modal is open
  const [pendingNoteColor, setPendingNoteColor] = useState<string | null>(null);

  const popoverRef = useRef<HTMLDivElement>(null);

  // Helper function to show alert modal
  const showAlert = (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success' = 'primary', isConfirmation = false, onConfirm?: () => void) => {
    setAlertModalConfig({
      title,
      message,
      buttonText: 'OK',
      buttonVariant,
      isConfirmation,
      onConfirm
    });
    setShowAlertModal(true);
  };

  const handleAddNoteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColorPopoverOpen(true);
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorPopoverOpen(false);
    setColorAnchorEl(null);
  };

  const handleColorSelect = async (color: string) => {
    if (!user) {
      addNote(color);
      trackEvent('sticky_note_created', { node_type: 'sticky_note' });
      handleColorClose();
      return;
    }

    // Check if canvas needs naming (no canvas ID)
    if (needsCanvasNaming(canvasId)) {
      // Store the color and show canvas name modal
      setPendingNoteColor(color);
      setShowCanvasNameModal(true);
      handleColorClose();
      return;
    }

    // Use existing canvas ID for saved canvases, or default for unsaved ones
    const effectiveCanvasId = canvasId || 'default';

    // Show loading state while checking quotas
    setIsCreatingNote(true);
    
    try {
      // Pre-check quotas before creating the note
      const canCreateStarter = await canCreateThreadStarter();
      
      if (!canCreateStarter) {
        // Show quota exceeded message
        showAlert(
          'Thread Starter Limit Reached',
          'You\'ve reached your thread starter limit. Please upgrade your plan to create more thread starters.',
          'danger'
        );
        return;
      }

      // Check if user can create a total note (per canvas limit)
      const canCreateTotal = await canCreateTotalNote();
      
      if (!canCreateTotal) {
        // Show quota exceeded message
        showAlert(
          'Canvas Note Limit Reached',
          'You\'ve reached your total notes limit for this canvas (20 notes). Please upgrade your plan or create a new canvas.',
          'danger'
        );
        return;
      }

      // Quotas are good, create the note
      addNote(color);
      trackEvent('sticky_note_created', { node_type: 'sticky_note' });
      handleColorClose();

      // Note: Quota will be incremented when persona is selected, not here
      // This prevents quota increment if user cancels persona selection

    } catch (error) {
      console.error('Error checking quota for note creation:', error);
      // Fallback: allow note creation even if quota check fails
      addNote(color);
      trackEvent('sticky_note_created', { node_type: 'sticky_note' });
      handleColorClose();
    } finally {
      setIsCreatingNote(false);
    }
  };

  const handleClearCanvas = () => {
    showAlert(
      'Clear Canvas',
      'Are you sure you want to clear all notes? This action cannot be undone.',
      'danger',
      true,
      () => {
        // Clear all notes
        const { state } = useWhiteboardStore.getState();
        state.notes.forEach(note => {
          useWhiteboardStore.getState().deleteNote(note.id);
        });
        showToast('Canvas cleared', 'success');
      }
    );
  };

  const handleSaveProject = () => {
    setShowSaveTreeModal(true);
  };

  const handleLoadProject = () => {
    setShowLoadTreeModal(true);
  };

  const handleHelpClick = () => {
    setShowHelpModal(true);
  };

  const handleCanvasNameSave = async (canvasName: string): Promise<void> => {
    if (!user || !pendingNoteColor) return;

    // Return a Promise that resolves when the entire process is complete
    return new Promise((resolve, reject) => {
      handleCanvasNaming(
        canvasName,
        user,
        showToast,
        showAlert,
        async (canvasId) => {
          // Canvas saved successfully, now check quotas before creating note
          const effectiveCanvasId = canvasId;

          try {
            // Pre-check quotas before creating the note
            const canCreateStarter = await canCreateThreadStarter();
            const canCreateTotal = await canCreateTotalNote();
            
            if (!canCreateStarter) {
              showToast('You\'ve reached your thread starter limit. Please upgrade your plan to create more thread starters.', 'error');
              setPendingNoteColor(null);
              reject(new Error('Thread starter limit reached'));
              return;
            }
            
            if (!canCreateTotal) {
              showToast('You\'ve reached your total notes limit for this canvas (20 notes). Please upgrade your plan or create a new canvas.', 'error');
              setPendingNoteColor(null);
              reject(new Error('Total notes limit reached'));
              return;
            }

            // Quotas are good, create the note
            addNote(pendingNoteColor);
            trackEvent('sticky_note_created', { node_type: 'sticky_note' });

            // Note: Quota will be incremented when persona is selected, not here
            // This prevents quota increment if user cancels persona selection

            // Clear pending color and resolve the promise
            setPendingNoteColor(null);
            setShowCanvasNameModal(false); // Close the canvas naming modal
            resolve();
          } catch (error) {
            console.error('Error creating note after canvas naming:', error);
            // Fallback: create note anyway
            addNote(pendingNoteColor);
            trackEvent('sticky_note_created', { node_type: 'sticky_note' });
            setPendingNoteColor(null);
            setShowCanvasNameModal(false); // Close the canvas naming modal
            resolve(); // Still resolve since note was created
          }
        },
        (error) => {
          // Canvas save failed, clear pending color and reject
          setPendingNoteColor(null);
          setShowCanvasNameModal(false); // Close the canvas naming modal
          showToast(`Failed to save canvas: ${error}`, 'error');
          reject(new Error(error));
        }
      );
    });
  };

  const handleCanvasNameCancel = () => {
    setShowCanvasNameModal(false);
    setPendingNoteColor(null);
  };

  const handleSaveTree = (filename: string) => {
    // Dispatch saving event to show animation
    window.dispatchEvent(new CustomEvent('whiteboard-saving', { 
      detail: { type: 'local', filename: filename } 
    }));

    const state = useWhiteboardStore.getState().state;

    const jsonContent = exportTreeToJSON(state);
    downloadJSON(jsonContent, filename);
    trackEvent('tree_exported', { export_format: 'json' });

    
    showToast('Project saved successfully!', 'success');
  };


  const getCurrentJsonContent = () => {
    const state = useWhiteboardStore.getState().state;
    return exportTreeToJSON(state);
  };

  const handleLoadTree = async (fileContent: string, canvasId?: string, canvasName?: string, description?: string, tags?: string[]) => {
    try {
      const newState = await importTreeFromFile(fileContent, canvasId);

      
      // Manually add the metadata to the state object
      if (canvasName) {
        newState.canvasName = canvasName;
      }
      if (description) {
        newState.description = description;
      }
      if (tags) {
        newState.tags = tags;
      }
      
      useWhiteboardStore.getState().loadTree(newState);
      showToast('Project loaded successfully!', 'success');

      trackEvent('tree_imported', { import_format: 'auto_detected' });

    } catch (error) {
      console.error('Error loading tree:', error);
      showToast('Failed to load project. Please check the file format.', 'error');
    }
  };


  // Close color popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        handleColorClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [colorPopoverOpen, colorAnchorEl]);

  // Hide toolbar when side chat is open
  if (isSideChatOpen) {
    return null;
  }

  return (
    <>
      <div className="mobile-toolbar">
        {/* Primary Actions */}
        <div className="mobile-toolbar-primary">
          <button
            className={`mobile-toolbar-button mobile-toolbar-button-primary ${isCreatingNote ? 'loading' : ''}`}
            onClick={handleAddNoteClick}
            disabled={isCreatingNote}
            title={isCreatingNote ? "Creating note..." : (notes.length === 0 ? "Get started - Create New Note" : "Create New Note")}
          >
            {isCreatingNote ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ animation: 'spin 1s linear infinite' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            ) : (
              <PlusIcon />
            )}
          </button>

          <button
            className="mobile-toolbar-button mobile-toolbar-button-secondary"
            onClick={handleClearCanvas}
            disabled={notes.length === 0}
            title="Clear Canvas"
          >
            <ClearIcon />
          </button>

          <button
            className="mobile-toolbar-button mobile-toolbar-button-secondary"
            onClick={handleSaveProject}
            title="Save Project"
          >
            <SaveIcon />
          </button>

          <button
            className="mobile-toolbar-button mobile-toolbar-button-secondary"
            onClick={handleLoadProject}
            title="Load Project"
          >
            <FolderIcon />
          </button>

          <button
            className="mobile-toolbar-button mobile-toolbar-button-secondary"
            onClick={handleHelpClick}
            title="Help Guide"
          >
            <HelpIcon />
          </button>
        </div>
      </div>

      {/* Color Popover */}
      {colorPopoverOpen && colorAnchorEl &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            className="mobile-color-popover"
            style={{ 
              position: 'fixed', 
              bottom: '100px', 
              left: '50%', 
              transform: 'translateX(-50%)', 
              zIndex: 2000 
            }}
          >
            <div className="mobile-color-grid">
              {GLASSMORPHIC_COLORS.map((color) => (
                <button
                  key={color.id}
                  className="mobile-color-option"
                  style={{ 
                    background: color.bg,
                    border: `2px solid ${color.border}`,
                  }}
                  onClick={() => handleColorSelect(color.solid)}
                  title={color.name}
                />
              ))}
            </div>
          </div>,
          document.body
        )
      }

      {/* Modals */}
      {showSaveTreeModal && (
        <SaveTreeModal
          isOpen={showSaveTreeModal}
          onClose={() => setShowSaveTreeModal(false)}
          onSave={handleSaveTree}

          yamlContent={getCurrentJsonContent()}

        />
      )}

      {showLoadTreeModal && (
        <LoadTreeModal
          isOpen={showLoadTreeModal}
          onClose={() => setShowLoadTreeModal(false)}
          onLoad={handleLoadTree}
        />
      )}

      {showCanvasNameModal && (
        <CanvasNameModal
          isOpen={showCanvasNameModal}
          onClose={handleCanvasNameCancel}
          onSave={handleCanvasNameSave}
        />
      )}

      {showHelpModal && (
        <HelpModal
          isOpen={showHelpModal}
          onClose={() => setShowHelpModal(false)}
        />
      )}

      {showAlertModal && (
        <AlertModal
          isOpen={showAlertModal}
          onClose={() => setShowAlertModal(false)}
          title={alertModalConfig.title}
          message={alertModalConfig.message}
          buttonText={alertModalConfig.buttonText}
          buttonVariant={alertModalConfig.buttonVariant}
          isConfirmation={alertModalConfig.isConfirmation}
          onConfirm={alertModalConfig.onConfirm}
        />
      )}
    </>
  );
};

export default MobileToolbar;
